import { API } from '../axios'
import { useMock, fetchPostmanMbp } from '../postman'

// Basic function that sets up all Authenticated User API calls.
export const users = async (props) => {
	const { url, method, headers, body, eCode, customError = false } = props
	const path = url !== undefined ? url : ''
	return await API({
		url: `users/${path}`,
		method,
		headers,
		body,
		eCode,
		customError
	})
}

export type RegistrationStep =
	| 'VerifyMember'
	| 'GuardianAttestation'
	| 'SendVerificationCode'
	| 'VerifyCode'
	| 'CreateAccount'
	| 'EmailOptions'
	| 'RegisterUser'

export type RegistrationUserType = 'Member' | 'Guardian' | ''

export interface RegistrationBody {
	stepType: RegistrationStep
	memberId: string | null
	ssn: string | null
	mbiNumber: string | null
	firstName: string
	middleName: string
	lastName: string
	dateOfBirth: string
	userName: string | null
	password: string | null
	emailAddress?: string | null
	mobilePhoneNumber?: string | null
	guardianFirstName?: string
	guardianMiddleName?: string
	guardianLastName?: string
	existingAppointment?: boolean
	isMarketingEmailEnabled?: boolean
	isMarketingTextEnabled?: boolean
	isEOBEmailEnabled?: boolean
	isEOBTextEnabled?: boolean
	isMoenLetterEmailEnabled?: boolean
	isMoenLetterTextEnabled?: boolean
	isCelesteEmailEnabled?: boolean
	isCelesteTextEnabled?: boolean
	isFullPolicyDocEmailEnabled?: boolean
	isFullPolicyDocTextEnabled?: boolean
	disclaimerAcceptance?: boolean
	registeringUserType?: RegistrationUserType
	termsAndConditionsVersion?: number
	verificationCodeReceiveType?: 'email' | 'text' | 'neither'
	verificationCode?: string
	verifyEmailAddress?: string
	verifyPhoneNumber?: string
}

type RegistrationNextStep =
	| 'SendVerificationCode'
	| 'CreateAccount'
	| 'EmailOptions'
	| 'RegisterUser'
type RegistrationCodes =
	| 'REG-0002'
	| 'REG-0003'
	| 'REG-0004'
	| 'REG-0005'
	| 'REG-0006'
	| 'REG-0007'
	| 'REG-0010'
	| 'REG-0013'
	| 'REG-0019'
	| 'REG-0021'
	| 'REG-0022'
	| 'REG-0023'
	| 'REG-0024'
	| 'REG-0025'
	| 'REG-0026'
	| 'REG-0027'
	| 'REG-0028'
	| 'REG-0040'
	| string

interface RegistrationError {
	code: RegistrationCodes
	propertyName: string
	message: string
}

export interface RegistrationResponse {
	nextStep: RegistrationNextStep
	allowEOBEmail: boolean
	isEOBEmailEnabled: boolean
	allowEOBText: boolean
	isEOBTextEnabled: boolean
	allowMarketingEmail: boolean
	isMarketingEmailEnabled: boolean
	allowMarketingText: boolean
	isMarketingTextEnabled: boolean
	allowMoenLetterEmail: boolean
	allowMoenLetterText: boolean
	isMoenLetterEmailEnabled: boolean
	isMoenLetterTextEnabled: boolean
	allowCelesteEmail: boolean
	allowCelesteText: boolean
	isCelesteEmailEnabled: boolean
	isCelesteTextEnabled: boolean
	allowFullPolicyDocEmail: boolean
	allowFullPolicyDocText: boolean
	isFullPolicyDocEmailEnabled: boolean
	isFullPolicyDocTextEnabled: boolean
	emailAddress: string | null
	phoneNumber: string | null
	validationErrors: RegistrationError[]
}

interface CustomRegistrationError {
	cause: {
		eCode: string
		response: {
			data: {
				validationErrors: {
					code: string
					propertyName: string
					message: string
				}[]
			}
			status?: number
			statusText?: 'Internal Server Error'
			headers?: object
			config?: object
			request?: XMLHttpRequest
		}
	}
}

// Type guard to check if error has validation errors
function hasValidationErrors(error: unknown): error is Error & {
	cause: RegistrationError[]
} {
	return (
		error instanceof Error &&
		Array.isArray(error.cause) &&
		error.cause.length > 0 &&
		'field' in error.cause[0]
	)
}

/**
 * Posts registration body, used to check each step and finally
 * submit the user for registration
 * @param body RegistrationBody
 * @returns RegistrationResponse
 */
export async function usersRegistration(
	body: Partial<RegistrationBody>,
	customError?: string
): Promise<RegistrationResponse | { error: RegistrationError[] }> {
	try {
		const response: RegistrationResponse = await API({
			url: 'public/users',
			method: 'POST',
			body: JSON.stringify(body),
			eCode: 'BP-NFA-003',
			customError: customError
		})

		if (response.validationErrors.length > 0) {
			return { error: response.validationErrors }
		}

		return response
	} catch (error: unknown) {
		const errorRes = (error as CustomRegistrationError).cause

		if (
			errorRes?.response?.data?.validationErrors?.length > 0 &&
			errorRes?.response?.data?.validationErrors?.[0]
		) {
			throw new Error(errorRes.response.data.validationErrors[0].code, {
				cause: errorRes.response.data.validationErrors
			})
		} else if (errorRes?.response?.status === 500) {
			throw new Error('Unknown error', {
				cause: errorRes
			})
		}
		throw error
	}
}

// Meant for user forgot user name
export const usersForgotName = async (body) => {
	return await API({
		url: 'public/users/forgotUserName',
		method: 'POST',
		body
	})
}

// Meant for user forgot user password
export const usersForgotPassword = async (body) => {
	return await API({
		url: 'public/users/forgotUserPassword',
		method: 'POST',
		body
	})
}

// Meant for changing passwords
export const usersChangePassword = async (body, user: string) => {
	return await users({
		url: `${user}/password`,
		method: 'PUT',
		body,
		eCode: 'BP-NFA-021'
	})
}

// Get Access Rights for a user with name and code
export const getUsersAccess = async (
	user: string,
	companyId: string,
	code: string,
	contractType: string
) => {
	return await users({
		url: `${user}/accessrights/${companyId}/${code}/${contractType}`,
		method: 'GET',
		eCode: 'BP-NFA-001'
	})
}

// Get Memberships of a particular user
export const getUsersMemberships = async (user: string) => {
	try {
		let results
		const params: MbpBody = {
			url: `${user}/memberships`,
			method: 'GET',
			eCode: 'BP-NFA-002'
		}

		if (useMock('MEMBERSHIPS')) {
			params.url = `users/${user}/memberships`
			results = await fetchPostmanMbp(params)
		} else {
			results = await users({
				...params
			})
		}
		return results
	} catch (error) {
		throw error
	}
}

// Delete a user
export const deleteUser = async (user: string) => {
	return await users({
		url: `${user}`,
		method: 'DELETE',
		eCode: 'BP-NFA-022'
	})
}
